/**
 * Which rail groups and folders are open, kept on the device. Defaults: FOLDERS open, a product's group open while it
 * holds five folders or fewer, and nested folders closed. A search opens a closed group that has a match, only while
 * searching. Storage can be missing or full (a private window): every read and write is guarded and the rail works
 * without it.
 */
import type { LibraryFolder } from "../../shared/types";
import { folderPath, groupOfFolder } from "./library";

export type GroupKey = "Folders" | "Flashkut" | "Flashroom";

export interface FoldState {
  groups: Partial<Record<GroupKey, boolean>>;
  /** Own folders opened to show their folders. */
  folders: string[];
}

const KEY = "fr-assets-open-v1";
/** A product's group starts open up to this many folders. */
const OPEN_UP_TO = 5;

export function loadFold(): FoldState {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? "null") as Partial<FoldState> | null;
    if (v && typeof v === "object") {
      return { groups: v.groups && typeof v.groups === "object" ? v.groups : {}, folders: Array.isArray(v.folders) ? v.folders.filter((x) => typeof x === "string") : [] };
    }
  } catch {
    // No storage, or something else wrote here: start from the defaults.
  }
  return { groups: {}, folders: [] };
}

export function saveFold(s: FoldState): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    // Not kept this time; the rail still works.
  }
}

export function groupOpen(s: FoldState, g: GroupKey, size: number, hit: boolean): boolean {
  return hit || (s.groups[g] ?? (g === "Folders" || size <= OPEN_UP_TO));
}

export function toggleGroup(s: FoldState, g: GroupKey, size: number, hit: boolean): FoldState {
  return { ...s, groups: { ...s.groups, [g]: !groupOpen(s, g, size, hit) } };
}

export function toggleFolder(s: FoldState, id: string): FoldState {
  return { ...s, folders: s.folders.includes(id) ? s.folders.filter((x) => x !== id) : [...s.folders, id] };
}

/** Opening a folder unfolds the way to it: its group, and for an own folder every folder above it. Same object when nothing changes. */
export function revealFolder(s: FoldState, folders: LibraryFolder[], id: string): FoldState {
  const f = folders.find((x) => x.id === id);
  if (!f) return s;
  const g = groupOfFolder(f) ?? "Folders";
  const need = folderPath(folders, id).slice(0, -1).map((x) => x.id).filter((x) => !s.folders.includes(x));
  // Why FOLDERS differs: it is open unless closed, while a product's group may be closed by its size.
  const open = g === "Folders" ? s.groups.Folders !== false : s.groups[g] === true;
  if (open && !need.length) return s;
  return { groups: { ...s.groups, [g]: true }, folders: need.length ? [...s.folders, ...need] : s.folders };
}
